import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Menu,
  CircularProgress,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { fetchAllSellers, updateUserStatus, AdminUser, AccountStatus } from "../../../State/admin/adminSlice";

const accStatus: { status: string; title: string }[] = [
  { status: "ALL", title: "All Statuses" },
  { status: "PENDING_VERIFICATION", title: "Pending Verification" },
  { status: "ACTIVE", title: "Active" },
  { status: "SUSPENDED", title: "Suspended" },
  { status: "DEACTIVATED", title: "Deactivated" },
  { status: "BANNED", title: "Banned" },
  { status: "CLOSED", title: "Closed" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const getStatusChipColor = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "PENDING_VERIFICATION":
      return "warning";
    case "SUSPENDED":
    case "DEACTIVATED":
    case "BANNED":
      return "error";
    default:
      return "default";
  }
};

const SellersTable = () => {
  const dispatch = useAppDispatch();
  const { sellers = [], loading } = useAppSelector((state: any) => state.admin || {});

  const [filterStatus, setFilterStatus] = useState("ALL");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSellerId, setSelectedSellerId] = useState<number | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("jwt");
    dispatch(fetchAllSellers(token || undefined));
  }, [dispatch]);

  const handleFilterChange = (event: any) => {
    setFilterStatus(event.target.value);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, sellerId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedSellerId(sellerId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSellerId(null);
  };

const handleStatusUpdate = (status: AccountStatus) => {
  if (selectedSellerId !== null) {
    dispatch(
      updateUserStatus({
        userId: selectedSellerId,
        status,
        role: "ROLE_SELLER", 
      })
    );
  }
  handleMenuClose();
};

  const filteredSellers =
    filterStatus === "ALL"
      ? sellers
      : sellers.filter((seller: AdminUser) => seller.accountStatus === filterStatus);

  return (
    <>
      <div className="pb-5 w-60">
        <FormControl fullWidth size="small">
          <InputLabel id="status-select-label">Filter Status</InputLabel>
          <Select
            labelId="status-select-label"
            id="status-select"
            value={filterStatus}
            label="Filter Status"
            onChange={handleFilterChange}
          >
            {accStatus.map((item) => (
              <MenuItem key={item.status} value={item.status}>
                {item.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <TableContainer component={Paper} className="shadow-sm border border-slate-200">
        <Table sx={{ minWidth: 700 }} aria-label="sellers table">
          <TableHead>
            <TableRow>
              <StyledTableCell>Seller Name</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell align="center">Mobile</StyledTableCell>
              <StyledTableCell align="center">Tax PIN</StyledTableCell>
              <StyledTableCell align="center">Business Name</StyledTableCell>
              <StyledTableCell align="center">Account Status</StyledTableCell>
              <StyledTableCell align="right">Change Status</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <StyledTableRow>
                <TableCell colSpan={7} align="center" className="py-8">
                  <CircularProgress size={32} />
                </TableCell>
              </StyledTableRow>
            ) : filteredSellers.length === 0 ? (
              <StyledTableRow>
                <TableCell colSpan={7} align="center" className="py-8 text-slate-500">
                  No sellers found for the selected status.
                </TableCell>
              </StyledTableRow>
            ) : (
              filteredSellers.map((seller: any) => (
                <StyledTableRow key={seller.id}>
                  <StyledTableCell component="th" scope="row">
                    {seller.sellerName || seller.name || "N/A"}
                  </StyledTableCell>
                  <StyledTableCell>{seller.email}</StyledTableCell>
                  <StyledTableCell align="center">{seller.mobile || "N/A"}</StyledTableCell>
                  <StyledTableCell align="center">
                    {seller.taxPin || seller.kraPin || seller.gstin || "N/A"}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {seller.businessDetails?.businessName || seller.businessName || "N/A"}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Chip
                      label={seller.accountStatus || "UNKNOWN"}
                      color={getStatusChipColor(seller.accountStatus) as any}
                      size="small"
                      className="font-semibold"
                    />
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => handleMenuOpen(e, seller.id)}
                    >
                      Update
                    </Button>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu for Updating Seller Status */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {accStatus
          .filter((item) => item.status !== "ALL")
          .map((item) => (
            <MenuItem
              key={item.status}
              onClick={() => handleStatusUpdate(item.status as AccountStatus)}
            >
              Set to {item.title}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

export default SellersTable;